import React from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Star, Phone, Quote, MapPin, CheckCircle, Camera } from 'lucide-react';
import { BUSINESS } from '@/lib/businessData';
import Seo from '@/components/Seo';

const TESTIMONIALS = [
  { name: 'Jennifer M.', neighborhood: 'Bridgeland', rating: 5, date: 'July 2026', text: 'My garage door spring snapped at 11pm with my car trapped inside. Mike had a tech at my house in 35 minutes and replaced both springs. Fair price, honest guy. This is who you call in Cypress.' },
  { name: 'Robert T.', neighborhood: 'Towne Lake', rating: 5, date: 'June 2026', text: 'Called three companies before Mike. Only one who answered on a Sunday. Fixed my off-track door and replaced the rollers in under an hour. Door has never been this quiet.' },
  { name: 'Sarah K.', neighborhood: 'Coles Crossing', rating: 5, date: 'June 2026', text: 'Mike installed our new LiftMaster opener and programmed it to our phones. Explained everything, cleaned up after, and the price was exactly what he quoted. No upsells.' },
  { name: 'David L.', neighborhood: 'Fairfield', rating: 5, date: 'May 2026', text: 'Dented panel from a backing-out accident. Thought I needed a whole new door. Mike matched and replaced just the damaged section — saved me thousands. True professional.' },
  { name: 'Maria G.', neighborhood: 'Cypress Creek', rating: 5, date: 'May 2026', text: 'Our opener stopped working the night before a road trip. Mike dispatched someone within the hour. New logic board installed and we made our trip. Lifesaver.' },
  { name: 'James W.', neighborhood: 'Cypress Lakes', rating: 5, date: 'April 2026', text: 'Full door replacement. Mike helped us pick an insulated steel door that cut our garage temperature dramatically. Installation was flawless and the crew was respectful of our home.' },
  { name: 'Patricia R.', neighborhood: 'Blackhorse Ranch', rating: 5, date: 'April 2026', text: 'Our heavy custom door needed special torsion springs. Mike\'s team knew exactly what gauge to use. Other companies were guessing — Mike knew. That confidence is why we chose him.' },
  { name: 'Thomas B.', neighborhood: 'Stonegate', rating: 5, date: 'March 2026', text: 'Safety sensors kept failing. Two other companies replaced them and they failed again. Mike found a wiring issue nobody else caught and fixed it permanently. Honest and thorough.' },
  { name: 'Lisa H.', neighborhood: 'Bridgeland', rating: 5, date: 'March 2026', text: 'New construction home in Bridgeland. Mike installed carriage-house doors that look amazing and operate silently. The smart-home integration with our phones was a bonus we didn\'t expect.' },
];

const PROJECT_PHOTOS = [
  { url: 'https://media.base44.com/images/public/6a5847080763d214450cfad8/96af05051_generated_ee7f2ed8.png', title: 'New Door Installation', location: 'Bridgeland, Cypress TX' },
  { url: 'https://media.base44.com/images/public/6a5847080763d214450cfad8/554e076ec_generated_8c3282b5.png', title: 'Torsion Spring Replacement', location: 'Towne Lake, Cypress TX' },
  { url: 'https://media.base44.com/images/public/6a5847080763d214450cfad8/ab0dff51f_generated_06592701.png', title: 'Opener Installation', location: 'Fairfield, Cypress TX' },
  { url: 'https://media.base44.com/images/public/6a5847080763d214450cfad8/1a55e3c9e_generated_829c7a39.png', title: 'Cable & Roller Replacement', location: 'Coles Crossing, Cypress TX' },
  { url: 'https://media.base44.com/images/public/6a5847080763d214450cfad8/a1463069b_generated_e64a94a0.png', title: 'Safety Sensor Repair', location: 'Cypress Creek, Cypress TX' },
  { url: 'https://media.base44.com/images/public/6a5847080763d214450cfad8/9399756fa_generated_9692e419.png', title: 'Emergency Off-Track Repair', location: 'Blackhorse Ranch, Cypress TX' },
];

export default function Testimonials() {
  return (
    <>
      <Seo
        title="Customer Reviews & Testimonials | Mike's Garage Door Repair Cypress TX"
        description="Read verified Google reviews from Cypress, TX homeowners who trusted Mike's Garage Door Repair for springs, openers, panels, and 24/7 emergency service. 4.9-star rated."
        path="/testimonials"
      />
      <Navbar />
      <main className="pt-16">
        {/* Hero */}
        <section className="bg-background border-b border-border tech-grid">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
            <div className="max-w-3xl">
              <div className="font-mono text-xs text-primary uppercase tracking-wider stencil mb-4">
                / Customer Reviews & Testimonials
              </div>
              <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-foreground text-balance leading-[0.95]">
                Cypress Trusts Mike's. <span className="text-primary">{BUSINESS.reviewCount} Reviews.</span>
              </h1>
              <p className="mt-6 text-lg text-muted-foreground max-w-2xl">
                Don't take our word for it. Read what your Cypress neighbors say about Mike's Garage Door Repair —
                from emergency spring replacements to full door installations.
              </p>
            </div>
          </div>
        </section>

        {/* Rating summary */}
        <section className="bg-card border-b border-border py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-6 h-6 fill-primary text-primary" />
                  ))}
                </div>
                <div className="font-heading font-black text-3xl text-primary">{BUSINESS.rating}</div>
                <div className="font-mono text-[10px] text-muted-foreground uppercase tracking-wider mt-1">Google Rating</div>
              </div>
              <div className="text-center">
                <div className="font-heading font-black text-3xl lg:text-4xl text-primary">{BUSINESS.reviewCount}</div>
                <div className="font-mono text-[10px] text-muted-foreground uppercase tracking-wider mt-1">Verified Reviews</div>
              </div>
              <div className="text-center">
                <div className="font-heading font-black text-3xl lg:text-4xl text-primary">5,200+</div>
                <div className="font-mono text-[10px] text-muted-foreground uppercase tracking-wider mt-1">Repairs Completed</div>
              </div>
              <div className="text-center">
                <div className="font-heading font-black text-3xl lg:text-4xl text-primary">{BUSINESS.yearsInBusiness}</div>
                <div className="font-mono text-[10px] text-muted-foreground uppercase tracking-wider mt-1">Years in Cypress</div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials grid */}
        <section className="bg-background py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-12">
              <div className="font-mono text-xs text-primary uppercase tracking-wider stencil mb-3">/ Verified Reviews</div>
              <h2 className="font-heading font-black text-3xl sm:text-4xl text-foreground text-balance">
                What Cypress Homeowners Say
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {TESTIMONIALS.map((review, idx) => (
                <article key={idx} className="bg-card border border-border p-6 flex flex-col">
                  <Quote className="w-8 h-8 text-primary/30 mb-3" />
                  <div className="flex gap-0.5 mb-3" aria-label={`${review.rating} out of 5 stars`}>
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-primary text-primary" />
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed flex-grow">"{review.text}"</p>
                  <div className="mt-4 pt-4 border-t border-border flex items-center gap-3">
                    <div className="w-9 h-9 bg-primary text-background flex items-center justify-center font-heading font-bold text-sm shrink-0">
                      {review.name.charAt(0)}
                    </div>
                    <div>
                      <div className="font-heading font-bold text-sm text-foreground">{review.name}</div>
                      <div className="font-mono text-[10px] text-muted-foreground flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {review.neighborhood}, Cypress TX
                      </div>
                    </div>
                    <div className="font-mono text-[10px] text-muted-foreground ml-auto">{review.date}</div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Project photos */}
        <section className="bg-card border-y border-border py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-12">
              <div className="flex items-center gap-2 mb-3">
                <Camera className="w-4 h-4 text-primary" />
                <div className="font-mono text-xs text-primary uppercase tracking-wider stencil">/ Recent Projects</div>
              </div>
              <h2 className="font-heading font-black text-3xl sm:text-4xl text-foreground text-balance">
                Recent Work Across Cypress
              </h2>
              <p className="mt-4 text-lg text-muted-foreground max-w-2xl">
                A look at garage door repairs and installations our team has completed in Cypress neighborhoods.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {PROJECT_PHOTOS.map((photo, idx) => (
                <div key={idx} className="group relative overflow-hidden border border-border">
                  <img
                    src={photo.url}
                    alt={`${photo.title} in ${photo.location}`}
                    className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="font-heading font-bold text-sm text-foreground">{photo.title}</div>
                    <div className="font-mono text-[10px] text-primary">{photo.location}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Trust badges */}
        <section className="bg-background py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-2xl text-foreground mb-8">Why Cypress Homeowners Choose Mike's</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                'Upfront, flat-rate pricing',
                'Same-day emergency response',
                'Licensed & insured technicians',
                'Parts & labor warranty',
              ].map((item) => (
                <div key={item} className="flex items-center gap-2 justify-center bg-card border border-border p-4">
                  <CheckCircle className="w-5 h-5 text-primary shrink-0" />
                  <span className="font-heading font-bold text-xs text-foreground">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-primary text-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="font-heading font-black text-2xl sm:text-3xl">Join 5,200+ Satisfied Cypress Customers</h2>
              <p className="font-mono text-sm opacity-80 mt-1">Experience the Mike's difference — honest, fast, done right.</p>
            </div>
            <div className="flex gap-3">
              <a href={`tel:${BUSINESS.phoneRaw}`} className="flex items-center gap-2 bg-background text-primary px-6 py-4 font-heading font-bold text-sm uppercase tracking-wider hover:opacity-90 transition-opacity">
                <Phone className="w-5 h-5" />
                {BUSINESS.phone}
              </a>
              <a href="/#booking" className="flex items-center gap-2 border-2 border-background text-background px-6 py-4 font-heading font-bold text-sm uppercase tracking-wider hover:bg-background hover:text-primary transition-all">
                Book Online
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}